import { useBlocker } from "@tanstack/react-router";
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";

type UnsavedChanges = {
  // Which sections have unsaved edits, updated as they render.
  dirtySections: Set<string>;
  // Lets the next navigation through without asking, after a create or a delete.
  skipBlocker: () => void;
};

const UnsavedChangesContext = createContext<UnsavedChanges | null>(null);

// One blocker for the whole panel, so leaving with unsaved details and stock asks only once.
export function UnsavedChangesProvider({
  message,
  children,
}: {
  message: string;
  children: ReactNode;
}) {
  const [dirtySections] = useState(() => new Set<string>());
  const skipRef = useRef(false);
  const shouldBlock = () => !skipRef.current && dirtySections.size > 0;

  useBlocker({
    // Staying on the same page, such as opening another section of the panel, loses nothing.
    shouldBlockFn: ({ current, next }) =>
      current.pathname !== next.pathname && shouldBlock() && !window.confirm(message),
    enableBeforeUnload: shouldBlock,
  });

  const [value] = useState<UnsavedChanges>(() => ({
    dirtySections,
    skipBlocker: () => {
      skipRef.current = true;
    },
  }));

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
}

// Reports a section's dirtiness during render. Call it with the value react-hook-form or local
// state gives on every render, so the blocker always reads the latest.
export function useUnsavedChanges(section: string, isDirty: boolean) {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error("useUnsavedChanges needs an UnsavedChangesProvider");
  }
  if (isDirty) {
    context.dirtySections.add(section);
  } else {
    context.dirtySections.delete(section);
  }
  // A section that goes away, such as the form for the reward just left, has nothing to lose.
  useEffect(() => () => void context.dirtySections.delete(section), [context, section]);
  return context.skipBlocker;
}
